import { Redirect, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Home } from "components/pages/Home/Home";
import { Session } from "components/pages/Session/Session";
import { NotFound } from "components/pages/NotFound/NotFound";
import { Login } from "components/pages/Home/Login";
import { OAuthHandler } from "components/shared/SignInButton/OAuthHandler";
import { ClassLinkLaunchpadInitiate } from "components/shared/SignInButton/ClassLinkLaunchpadInitiate";
import {
  ConsensusQuestionRender,
} from "components/pages/Session/Activity/activities/ConsensusActivity/Edit/ConsensusQuestionRender";
import {
  ConsensusQuestionView
} from "components/pages/Session/Activity/activities/ConsensusActivity/Edit/ConsensusQuestionView";
import {
  CrazyCalculatorQuestionRender
} from "components/pages/Session/Activity/activities/CalculatorActivity/Edit/CrazyCalculatorQuestionRender";
import {
  CrazyCalculatorQuestionView
} from "components/pages/Session/Activity/activities/CalculatorActivity/Edit/CrazyCalculatorQuestionView";
import { GroupCodeView } from "components/pages/Educator/Dashboard/GroupCodeView";
import { GroupsHeader } from "components/pages/Educator/Dashboard/GroupsHeader";
import { ManageGroupsMain } from "components/pages/Educator/Dashboard/ManageGroupsMain";
import { ClassReports } from "components/pages/Educator/ClassReports/ClassReports";
import { SidebarView } from "components/SidebarView";
import { colors, constants } from "utils";
import { SessionHistory } from "components/pages/Educator/Session/SessionHistory";
import {
  LiveSessionsPage,
  LiveSessionsHeader,
} from "components/pages/Educator/Session/LiveSessions/LiveSessionsPage";
import { SettingsModal } from "components/pages/Educator/SignUp/SettingsModal";
import { Observe } from "components/pages/Session/Observe/Observe";
import { SessionSummaryPromptConsole } from "components/pages/Admin/SessionSummaryPromptConsole";
import { EdnitionAdminConsole } from "components/pages/Admin/EdnitionAdmin";
import { FeatureFlagsAdminConsole } from "components/pages/Admin/FeatureFlagsAdmin";
import { GroupingSimulationConsole } from "components/pages/Admin/GroupingSimulation";
import { FacilitationAIPromptConsole } from "components/pages/Admin/FacilitationAIPromptConsole";
import { ExplanationAIPromptConsole } from "components/pages/Admin/ExplanationAIPromptConsole";
import { Reports } from "components/pages/Admin/Reports";
import { CSReports } from "components/pages/Admin/CSReports";
import { TeacherReportsMetrics } from "components/pages/Admin/TeacherReportsMetrics";
import { SessionReplay } from "components/pages/Admin/SessionReplay";
import { SemanticSearch } from "components/pages/Admin/SemanticSearch";
import { SessionAnalyzer } from "components/pages/Admin/SessionAnalyzer";
import { TaggerExperimentsComparison } from "components/pages/Admin/TaggerExperimentsComparison";
import { StudentFeedbackExperimentsComparison } from "components/pages/Admin/StudentFeedbackExperimentsComparison";
import { BetterHintExperimentsComparison } from "components/pages/Admin/BetterHintExperimentsComparison";
import {
  ClientList,
  ClientDetail,
  ClientCreate,
  ClientEdit,
  SchoolDetail,
  SchoolCreate,
  SchoolEdit,
} from "components/pages/Admin/ClientAdmin";
import {
  UserList,
  UserDetail,
  UserEdit,
} from "components/pages/Admin/UserAdmin";
import { GroupCodeStandardSearch } from "components/pages/GroupCodeStandardSearch/GroupCodeStandardSearch";
import { LessonsStandards } from "components/pages/LessonsStandards/LessonsStandards";
import { CreateQuickGroup } from "components/pages/CreateQuickGroup/CreateQuickGroup";
import { WarmUpPickerRoute } from "components/pages/WarmUpPicker/WarmUpPicker";
import { ViewChangeLesson } from "components/pages/ViewChangeLesson/ViewChangeLesson";
import { ViewChangeStandard } from "components/pages/ViewChangeStandard/ViewChangeStandard";
import { EditGroupsPage } from "components/pages/Educator/Dashboard/EditGroupsPage";
import { EditGroupFocus } from "components/pages/EditGroupFocus/EditGroupFocus";
import { LessonEditGroupFocus } from "components/pages/EditGroupFocus/LessonEditGroupFocus";
import { QuestionSetView } from "components/pages/QuestionSet/QuestionSetView";
import { SessionHeader } from "components/pages/Educator/Session/SessionHeader/SessionHeader";
import { WeeklyReport } from "components/pages/Educator/WeeklyReport/WeeklyReport";
import { QuestionHumanAnalyze } from "components/pages/Admin/QuestionHumanAnalyze";
import { EvaluatorReview } from "components/pages/Admin/EvaluatorReview/EvaluatorReview";
import { EvaluatorFullView } from "components/pages/Admin/EvaluatorReview/EvaluatorFullView";
import { LessonDetailView } from "components/pages/Admin/EvaluatorReview/LessonDetailView";
import { EvaluationToolsGuard } from "components/pages/Admin/EvaluatorReview/EvaluationToolsGuard";
import { LcAlignmentReview } from "components/pages/Admin/EvaluatorReview/LcAlignmentReview";
import { ItemSelection, LlmTags, TagPrompt, SuperuserGuard, StaffGuard } from "components/pages/Admin/SkillsProfile";
import { WarmUpSetsAdminConsole } from "components/pages/Admin/WarmUpSetsAdmin";
import { SkillsProfileReview } from "components/pages/Admin/SkillsProfile/SkillsProfileReview";
import { ImQuestionBrowser } from "components/pages/Admin/ImQuestionBrowser";
import { AdminWeeklyReportTabs } from "components/pages/Admin/AdminWeeklyReportTabs";
import { StudentReports } from "components/pages/Educator/StudentReports/StudentReports";
import { ResourceHub } from "components/pages/Educator/ResourceHub/ResourceHub";
import { SchoolReports } from "components/pages/Educator/SchoolReports/SchoolReports";
import { SchoolReportsLanding } from "components/pages/Educator/SchoolReports/SchoolReportsLanding";

const AdminViewContainer = styled.div`
  background-color: ${colors.milkGlass200};
  padding: 24px;
  min-height: calc(100vh - 92px);
`;

const SessionHistoryContainer = styled.div`
  background-color: ${colors.milkGlass200};
  padding: 24px;
  min-height: calc(100vh - 92px);
`;

// School/district admins land on their reports page instead of the teacher
// dashboard — once per browser session, with all the fail-open rules in
// SchoolReportsLanding.
const EducatorHome = () => (
  <SchoolReportsLanding>
    <SidebarView sideVariant="educator" viewComponent={
      <ManageGroupsMain>
        <GroupCodeView />
      </ManageGroupsMain>} headerTitle={<GroupsHeader />}>
    </SidebarView>
  </SchoolReportsLanding>
);

// The three class-level reports moved under one tabbed page; their old URLs
// live on in bookmarks, Class Insights deep links and analytics, so they land
// on the matching tab instead of a 404.
const ClassReportTabRedirect = ({ tab }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("tab", tab);
  return <Redirect to={{ pathname: constants.CLASS_REPORTS_PATH, search: `?${params.toString()}` }} />;
};

export const routes = [
  {
    name: "OAuthCallback",
    path: "/auth/callback",
    exact: true,
    component: OAuthHandler,
  },
  {
    name: "ClassLinkLaunchpadInitiate",
    path: "/auth/classlink/initiate",
    exact: true,
    component: ClassLinkLaunchpadInitiate,
  },
  {
    name: "Home",
    path: "/",
    exact: true,
    component: Home,
  },
  {
    name: "Timeout",
    path: "/timeout",
    exact: true,
    component: () => <Home timeout />
  },
  {
    name: "Session",
    path: "/session/:sessionId",
    component: Session,
  },
  {
    path: constants.EDUCATOR_SETTINGS_PATH,
    name: constants.EDUCATOR_SETTINGS_ROUTE_NAME,
    component: () => <SettingsModal isOpen isOnboarding setIsOpen={() => { }} />,
  },
  {
    path: "/educator/signup",
    component: () => <Login redirectPath={constants.EDUCATOR_SETTINGS_ROUTE_NAME} isSignUp authOptions={[constants.GOOGLE, constants.CLEVER, constants.CLASSLINK, constants.MICROSOFT]} />,
  },
  {
    path: constants.EDUCATOR_LOGIN_PATH,
    name: constants.EDUCATOR_LOGIN_ROUTE_NAME,
    component: () => <Login pathName={constants.EDUCATOR_LOGIN_ROUTE_NAME} redirectPath={constants.EDUCATOR_ROUTE_NAME} authOptions={[constants.GOOGLE, constants.CLEVER, constants.CLASSLINK, constants.MICROSOFT]} />,
  },
  {
    path: constants.ADMIN_LOGIN_PATH,
    name: constants.ADMIN_LOGIN_ROUTE_NAME,
    component: () => <Login redirectPath={constants.SESSION_SUMMARY_PROMPT_CONSOLE_ROUTE_NAME} authOptions={[constants.GOOGLE]} returnToAdminNext />,
  },
  {
    path: constants.EDUCATOR_PATH,
    name: constants.EDUCATOR_ROUTE_NAME,
    component: EducatorHome,
  },
  {
    path: constants.SCHOOL_REPORTS_PATH,
    name: constants.SCHOOL_REPORTS_ROUTE_NAME,
    component: () => <SidebarView sideVariant="educator" viewComponent={
      <SchoolReports />
    }>
    </SidebarView>,
  },
  {
    path: constants.SESSION_HISTORY_PATH,
    name: constants.SESSION_HISTORY_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="educator" viewComponent={
        <SessionHistoryContainer>
          <SessionHistory />
        </SessionHistoryContainer>} headerTitle={<SessionHeader />}>
      </SidebarView>
    </>,
  },
  {
    path: constants.LIVE_SESSIONS_PATH,
    name: constants.LIVE_SESSIONS_ROUTE_NAME,
    component: () => (
      <SidebarView
        sideVariant="educator"
        viewComponent={
          <SessionHistoryContainer>
            <LiveSessionsPage />
          </SessionHistoryContainer>
        }
        headerTitle={<LiveSessionsHeader />}
      />
    ),
  },
  {
    path: constants.WEEKLY_REPORTS_PATH,
    name: constants.WEEKLY_REPORTS_ROUTE_NAME,
    component: () => <SidebarView sideVariant="educator" viewComponent={
      <WeeklyReport />
    }>
    </SidebarView>,
  },
  {
    path: constants.STUDENT_REPORTS_PATH,
    name: constants.STUDENT_REPORTS_ROUTE_NAME,
    component: () => <SidebarView sideVariant="educator" viewComponent={
      <StudentReports />
    }>
    </SidebarView>,
  },
  {
    path: constants.CLASS_REPORTS_PATH,
    name: constants.CLASS_REPORTS_ROUTE_NAME,
    component: () => <SidebarView sideVariant="educator" viewComponent={
      <ClassReports />
    }>
    </SidebarView>,
  },
  {
    path: constants.STUDENT_ACTIVITY_PATH,
    name: constants.STUDENT_ACTIVITY_ROUTE_NAME,
    component: () => <ClassReportTabRedirect tab="dosage" />,
  },
  {
    path: constants.PROFICIENCY_PATH,
    name: constants.PROFICIENCY_ROUTE_NAME,
    component: () => <ClassReportTabRedirect tab="proficiency" />,
  },
  {
    path: constants.RESOURCE_HUB_PATH,
    name: constants.RESOURCE_HUB_ROUTE_NAME,
    component: () => <SidebarView sideVariant="educator" viewComponent={
      <ResourceHub />
    } headerTitle={constants.RESOURCE_HUB}>
    </SidebarView>,
  },
  {
    path: constants.COLLABORATION_TREND_PATH,
    name: constants.COLLABORATION_TREND_ROUTE_NAME,
    component: () => <ClassReportTabRedirect tab="collaboration" />,
  },
  {
    path: constants.SESSION_SUMMARY_PROMPT_CONSOLE_PATH,
    name: constants.SESSION_SUMMARY_PROMPT_CONSOLE_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <SessionSummaryPromptConsole />
        </AdminViewContainer>} headerTitle={constants.SESSION_SUMMARY_PROMPT_CONSOLE}>
      </SidebarView>
    </>,
  },
  {
    path: constants.EDNITION_ADMIN_PATH,
    name: constants.EDNITION_ADMIN_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <EdnitionAdminConsole />
        </AdminViewContainer>} headerTitle={constants.EDNITION_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.FEATURE_FLAGS_ADMIN_PATH,
    name: constants.FEATURE_FLAGS_ADMIN_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <FeatureFlagsAdminConsole />
        </AdminViewContainer>} headerTitle={constants.FEATURE_FLAGS_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.GROUPING_SIMULATION_PATH,
    name: constants.GROUPING_SIMULATION_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <GroupingSimulationConsole />
        </AdminViewContainer>} headerTitle={constants.GROUPING_SIMULATION}>
      </SidebarView>
    </>,
  },
  {
    path: constants.FACILITATION_AI_PROMPT_CONSOLE_PATH,
    name: constants.FACILITATION_AI_PROMPT_CONSOLE_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <FacilitationAIPromptConsole />
        </AdminViewContainer>} headerTitle={constants.FACILITATION_AI_PROMPT_CONSOLE}>
      </SidebarView>
    </>,
  },
  {
    path: constants.EXPLANATION_AI_PROMPT_CONSOLE_PATH,
    name: constants.EXPLANATION_AI_PROMPT_CONSOLE_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <ExplanationAIPromptConsole />
        </AdminViewContainer>} headerTitle={constants.EXPLANATION_AI_PROMPT_CONSOLE}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CONSENSUS_QUESTIONS_VIEW_PATH,
    name: constants.CONSENSUS_QUESTIONS_VIEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <ConsensusQuestionView />
        </AdminViewContainer>} headerTitle={constants.CONSENSUS_QUESTIONS_VIEW}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CONSENSUS_QUESTIONS_RENDER_PATH,
    name: constants.CONSENSUS_QUESTIONS_RENDER_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <ConsensusQuestionRender />
        </AdminViewContainer>} headerTitle={constants.CONSENSUS_QUESTIONS_RENDER}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CRAZY_CALCULATOR_QUESTIONS_VIEW_PATH,
    name: constants.CRAZY_CALCULATOR_QUESTIONS_VIEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <CrazyCalculatorQuestionView />
        </AdminViewContainer>} headerTitle={constants.CRAZY_CALCULATOR_QUESTIONS_VIEW}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CRAZY_CALCULATOR_QUESTIONS_RENDER_PATH,
    name: constants.CRAZY_CALCULATOR_QUESTIONS_RENDER_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <CrazyCalculatorQuestionRender />
        </AdminViewContainer>} headerTitle={constants.CRAZY_CALCULATOR_QUESTIONS_RENDER}>
      </SidebarView>
    </>,
  },
  {
    path: constants.WARM_UP_SETS_ADMIN_PATH,
    name: constants.WARM_UP_SETS_ADMIN_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <StaffGuard>
          <AdminViewContainer>
            <WarmUpSetsAdminConsole />
          </AdminViewContainer>
        </StaffGuard>} headerTitle={constants.WARM_UP_SETS_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.REPORTS_PATH,
    name: constants.REPORTS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <Reports />
        </AdminViewContainer>} headerTitle={constants.REPORTS}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CS_REPORTS_PATH,
    name: constants.CS_REPORTS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <CSReports />
        </AdminViewContainer>} headerTitle={constants.CS_REPORTS}>
      </SidebarView>
    </>,
  },
  {
    path: constants.TEACHER_REPORTS_METRICS_PATH,
    name: constants.TEACHER_REPORTS_METRICS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <TeacherReportsMetrics />
        </AdminViewContainer>} headerTitle={constants.TEACHER_REPORTS_METRICS}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SESSION_REPLAY_PATH,
    name: constants.SESSION_REPLAY_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <SessionReplay />
        </AdminViewContainer>} headerTitle={constants.SESSION_REPLAY}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SEMANTIC_SEARCH_PATH,
    name: constants.SEMANTIC_SEARCH_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <SemanticSearch />
        </AdminViewContainer>} headerTitle={constants.SEMANTIC_SEARCH}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SESSION_ANALYZER_PATH,
    name: constants.SESSION_ANALYZER_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <SessionAnalyzer />
        </AdminViewContainer>} headerTitle={constants.SESSION_ANALYZER}>
      </SidebarView>
    </>,
  },
  {
    path: constants.TAGGER_PROMPT_EXPERIMENTS_PATH,
    name: constants.TAGGER_PROMPT_EXPERIMENTS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <TaggerExperimentsComparison />
        </AdminViewContainer>} headerTitle={`${constants.EXPERIMENTS_COMPARISON} — ${constants.TAGGER_PROMPT_EXPERIMENTS}`}>
      </SidebarView>
    </>,
  },
  {
    path: constants.STUDENT_FEEDBACK_EXPERIMENTS_PATH,
    name: constants.STUDENT_FEEDBACK_EXPERIMENTS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <StudentFeedbackExperimentsComparison />
        </AdminViewContainer>} headerTitle={`${constants.EXPERIMENTS_COMPARISON} — ${constants.STUDENT_FEEDBACK_EXPERIMENTS}`}>
      </SidebarView>
    </>,
  },
  {
    path: constants.BETTER_HINT_EXPERIMENTS_PATH,
    name: constants.BETTER_HINT_EXPERIMENTS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <BetterHintExperimentsComparison />
        </AdminViewContainer>} headerTitle={`${constants.EXPERIMENTS_COMPARISON} — ${constants.BETTER_HINT_EXPERIMENTS}`}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CLIENT_ADMIN_NEW_PATH,
    name: constants.CLIENT_ADMIN_NEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <ClientCreate />
        </AdminViewContainer>} headerTitle={constants.CLIENT_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CLIENT_ADMIN_EDIT_PATH,
    name: constants.CLIENT_ADMIN_EDIT_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <ClientEdit />
        </AdminViewContainer>} headerTitle={constants.CLIENT_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SCHOOL_ADMIN_NEW_PATH,
    name: constants.SCHOOL_ADMIN_NEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <SchoolCreate />
        </AdminViewContainer>} headerTitle={constants.CLIENT_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SCHOOL_ADMIN_EDIT_PATH,
    name: constants.SCHOOL_ADMIN_EDIT_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <SchoolEdit />
        </AdminViewContainer>} headerTitle={constants.CLIENT_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SCHOOL_ADMIN_DETAIL_PATH,
    name: constants.SCHOOL_ADMIN_DETAIL_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <SchoolDetail />
        </AdminViewContainer>} headerTitle={constants.CLIENT_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CLIENT_ADMIN_DETAIL_PATH,
    name: constants.CLIENT_ADMIN_DETAIL_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <ClientDetail />
        </AdminViewContainer>} headerTitle={constants.CLIENT_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CLIENT_ADMIN_PATH,
    name: constants.CLIENT_ADMIN_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <ClientList />
        </AdminViewContainer>} headerTitle={constants.CLIENT_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.USER_ADMIN_EDIT_PATH,
    name: constants.USER_ADMIN_EDIT_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <UserEdit />
        </AdminViewContainer>} headerTitle={constants.USER_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.USER_ADMIN_DETAIL_PATH,
    name: constants.USER_ADMIN_DETAIL_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <UserDetail />
        </AdminViewContainer>} headerTitle={constants.USER_ADMIN}>
      </SidebarView>
    </>,
  },
  {
    path: constants.USER_ADMIN_PATH,
    name: constants.USER_ADMIN_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <UserList />
        </AdminViewContainer>} headerTitle={constants.USER_ADMIN}>
      </SidebarView>
    </>
  },
  {
    path: constants.QUESTION_HUMAN_ANALYZE_PATH,
    name: constants.QUESTION_HUMAN_ANALYZE_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminViewContainer>
          <QuestionHumanAnalyze />
        </AdminViewContainer>} headerTitle={constants.QUESTION_HUMAN_ANALYZE}>
      </SidebarView>
    </>,
  },
  {
    path: constants.EVALUATOR_REVIEW_FULL_VIEW_PATH,
    name: constants.EVALUATOR_REVIEW_FULL_VIEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <EvaluationToolsGuard>
          <AdminViewContainer>
            <EvaluatorFullView />
          </AdminViewContainer>
        </EvaluationToolsGuard>} headerTitle={constants.EVALUATOR_REVIEW}>
      </SidebarView>
    </>,
  },
  {
    path: constants.EVALUATOR_REVIEW_LESSON_PATH,
    name: constants.EVALUATOR_REVIEW_LESSON_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <EvaluationToolsGuard>
          <AdminViewContainer>
            <LessonDetailView />
          </AdminViewContainer>
        </EvaluationToolsGuard>} headerTitle={constants.EVALUATOR_REVIEW}>
      </SidebarView>
    </>,
  },
  {
    path: constants.EVALUATOR_LC_REVIEW_PATH,
    name: constants.EVALUATOR_LC_REVIEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <EvaluationToolsGuard>
          <AdminViewContainer>
            <LcAlignmentReview />
          </AdminViewContainer>
        </EvaluationToolsGuard>} headerTitle={constants.EVALUATOR_LC_REVIEW}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SKILLS_PROFILE_REVIEW_PATH,
    name: constants.SKILLS_PROFILE_REVIEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <EvaluationToolsGuard>
          <AdminViewContainer>
            <SkillsProfileReview />
          </AdminViewContainer>
        </EvaluationToolsGuard>} headerTitle={constants.SKILLS_PROFILE}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SKILLS_PROFILE_ITEM_SELECTION_PATH,
    name: constants.SKILLS_PROFILE_ITEM_SELECTION_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <SuperuserGuard>
          <AdminViewContainer>
            <ItemSelection />
          </AdminViewContainer>
        </SuperuserGuard>} headerTitle={constants.SKILLS_PROFILE_ITEM_SELECTION}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SKILLS_PROFILE_PROMPTS_PATH,
    name: constants.SKILLS_PROFILE_PROMPTS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <SuperuserGuard>
          <AdminViewContainer>
            <TagPrompt />
          </AdminViewContainer>
        </SuperuserGuard>} headerTitle={constants.SKILLS_PROFILE_PROMPTS}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SKILLS_PROFILE_LLM_TAGS_PATH,
    name: constants.SKILLS_PROFILE_LLM_TAGS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <SuperuserGuard>
          <AdminViewContainer>
            <LlmTags />
          </AdminViewContainer>
        </SuperuserGuard>} headerTitle={constants.SKILLS_PROFILE_LLM_TAGS}>
      </SidebarView>
    </>,
  },
  {
    path: constants.SKILLS_PROFILE_PATH,
    exact: true,
    component: () => <Redirect to={constants.SKILLS_PROFILE_REVIEW_PATH} />,
  },
  {
    path: constants.EVALUATOR_REVIEW_PATH,
    name: constants.EVALUATOR_REVIEW_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <EvaluationToolsGuard>
          <AdminViewContainer>
            <EvaluatorReview />
          </AdminViewContainer>
        </EvaluationToolsGuard>} headerTitle={constants.EVALUATOR_REVIEW}>
      </SidebarView>
    </>,
  },
  {
    path: constants.IM_QUESTION_BROWSER_PATH,
    name: constants.IM_QUESTION_BROWSER_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <EvaluationToolsGuard>
          <AdminViewContainer>
            <ImQuestionBrowser />
          </AdminViewContainer>
        </EvaluationToolsGuard>} headerTitle={constants.IM_QUESTION_BROWSER}>
      </SidebarView>
    </>,
  },
  {
    path: constants.ADMIN_WEEKLY_REPORTS_PATH,
    name: constants.ADMIN_WEEKLY_REPORTS_ROUTE_NAME,
    component: () => <>
      <SidebarView sideVariant="admin" viewComponent={
        <AdminWeeklyReportTabs />}>
      </SidebarView>
    </>,
  },
  {
    path: constants.CUSTOM_REPORT_PATH,
    component: () => <Redirect to={constants.ADMIN_WEEKLY_REPORTS_PATH} />,
  },
  {
    path: "/admin*",
    component: () => <Redirect to={constants.SESSION_SUMMARY_PROMPT_CONSOLE_PATH} />,
  },
  {
    path: "/observe/:groupCode",
    name: "Observe",
    component: Observe,
  },
  {
    path: constants.LESSONS_STANDARDS_PATH,
    name: constants.LESSONS_STANDARDS_ROUTE_NAME,
    component: LessonsStandards,
  },
  {
    path: constants.VIEW_CHANGE_LESSON_PATH,
    name: constants.VIEW_CHANGE_LESSON_ROUTE_NAME,
    component: ViewChangeLesson,
  },
  {
    path: constants.VIEW_CHANGE_STANDARD_PATH,
    name: constants.VIEW_CHANGE_STANDARD_ROUTE_NAME,
    component: ViewChangeStandard,
  },
  {
    path: constants.EDIT_GROUPS_PATH,
    name: constants.EDIT_GROUPS_ROUTE_NAME,
    component: () => <SidebarView sideVariant="educator" viewComponent={
      <EditGroupsPage />
    } headerTitle={constants.DASHBOARD}>
    </SidebarView>,
  },
  {
    path: constants.EDIT_GROUP_FOCUS_PATH,
    name: constants.EDIT_GROUP_FOCUS_ROUTE_NAME,
    component: EditGroupFocus,
  },
  {
    path: constants.LESSON_EDIT_GROUP_FOCUS_PATH,
    name: constants.LESSON_EDIT_GROUP_FOCUS_ROUTE_NAME,
    component: LessonEditGroupFocus,
  },
  {
    path: constants.QUESTION_SET_PATH,
    name: constants.QUESTION_SET_ROUTE_NAME,
    component: QuestionSetView,
  },
  {
    path: constants.GROUP_CODE_STANDARD_SEARCH_PATH,
    name: constants.GROUP_CODE_STANDARD_SEARCH_ROUTE_NAME,
    component: GroupCodeStandardSearch,
  },
  {
    path: constants.CREATE_QUICK_GROUP_PATH,
    name: constants.CREATE_QUICK_GROUP_ROUTE_NAME,
    component: CreateQuickGroup,
  },
  {
    path: constants.WARM_UP_PICKER_PATH,
    name: constants.WARM_UP_PICKER_ROUTE_NAME,
    component: WarmUpPickerRoute,
  },
  {
    component: NotFound,
  },
];
